import React, { useState, useEffect, useRef } from 'react';
import { Check, Copy, X, RefreshCw, AlertTriangle, FileText, Table, Users, CheckCircle } from 'lucide-react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import EmailNewsletterActionModal from '../components/EmailNewsletterActionModal';

// LocalStorage key for persisting newsletter data
const NEWSLETTER_STORAGE_KEY = 'us_citrus_ai_email_newsletters';

// Utility functions for localStorage
const saveNewslettersToStorage = (newsletters: NewsletterRow[], runningJobs?: Map<string, string>) => {
  try {
    // Check if localStorage is available
    if (typeof Storage === 'undefined') return;

    const dataToSave = {
      rows: newsletters.map(row => ({
        ...row,
        createdAt: row.createdAt.toISOString() // Convert Date to string for storage
      })),
      runningJobs: runningJobs ? Object.fromEntries(runningJobs) : {}
    };
    localStorage.setItem(NEWSLETTER_STORAGE_KEY, JSON.stringify(dataToSave));
  } catch (error) {
    console.error('Failed to save newsletters to localStorage:', error);
  }
};

const loadNewslettersFromStorage = (): { rows: NewsletterRow[]; runningJobs: Map<string, string> } => {
  try {
    // Check if localStorage is available
    if (typeof Storage === 'undefined') return { rows: [], runningJobs: new Map() };

    const stored = localStorage.getItem(NEWSLETTER_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      
      // Handle both old format (array) and new format (object with rows and runningJobs)
      if (Array.isArray(parsed)) {
        // Old format - just rows array
        return {
          rows: parsed.map((row: any) => ({
            ...row,
            createdAt: new Date(row.createdAt) // Convert string back to Date
          })),
          runningJobs: new Map()
        };
      } else if (parsed && Array.isArray(parsed.rows)) {
        // New format - object with rows and runningJobs
        const jobsMap = parsed.runningJobs && typeof parsed.runningJobs === 'object'
          ? new Map(Object.entries(parsed.runningJobs) as [string, string][])
          : new Map();
        
        return {
          rows: parsed.rows.map((row: any) => ({
            ...row,
            createdAt: new Date(row.createdAt) // Convert string back to Date
          })),
          runningJobs: jobsMap
        };
      }
    }
  } catch (error) {
    console.error('Failed to load newsletters from localStorage:', error);
  }
  return { rows: [], runningJobs: new Map() };
};

const clearNewslettersFromStorage = () => {
  try {
    localStorage.removeItem(NEWSLETTER_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear newsletters from localStorage:', error);
  }
};

/**
 * Email Newsletter Agent Page
 * 
 * Features:
 * - Job title selection from comprehensive dropdown (500+ titles)
 * - Generate 5 targeted newsletters per job title using US Citrus/MCP data
 * - Table view with status tracking and bulk operations
 * - Individual newsletter editing and export capabilities
 * - Google Docs/Sheets integration for content export
 * - Professional UI matching BlogCreatorPage and CompetitorConquestingPage patterns
 * 
 * Why this matters: Provides Senior Lifecycle Marketing Managers with high-quality
 * email sequences tailored to specific ICPs using real US Citrus email performance data.
 */

// Email performance data interface from MCP
interface EmailPerformanceData {
  totalEmails: number;
  totalDelivered: number;
  totalOpened: number;
  totalReplied: number;
  openingRate: number;
  replyRate: number;
}

// Newsletter row interface for table management
interface NewsletterRow {
  id: string;
  targetAudience: string;
  jobTitle: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  progress: string;
  newsletters: string[];
  heroCTA?: string;
  mainCTA?: string;
  mcpData?: EmailPerformanceData;
  createdAt: Date;
  error?: string;
}

const EmailNewsletterPage: React.FC = () => {
  // State management
  const [selectedTargetAudience, setSelectedTargetAudience] = useState<string>('');
  const [selectedJobTitle, setSelectedJobTitle] = useState<string>('');
  const [newsletterRows, setNewsletterRows] = useState<NewsletterRow[]>(() => {
    const loaded = loadNewslettersFromStorage();
    return loaded.rows;
  });
  const [runningJobs, setRunningJobs] = useState<Map<string, string>>(() => {
    const loaded = loadNewslettersFromStorage();
    return loaded.runningJobs;
  });
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [showTargetDropdown, setShowTargetDropdown] = useState<boolean>(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const activePollingRef = useRef<Set<string>>(new Set());
  
  // Modal state
  const [isActionModalOpen, setIsActionModalOpen] = useState<boolean>(false);
  const [activeModalRowId, setActiveModalRowId] = useState<string | null>(null);
  
  
  // Sorting
  const [sortField, setSortField] = useState<keyof NewsletterRow | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // References
  const dropdownRef = useRef<HTMLDivElement>(null);
  const targetDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Target audience options for lifecycle marketing
  const targetAudiences = [
    'Sales Leaders',
    'SDRs',
    'BDRs', 
    'AEs',
    'CEO',
    'Founder / Co-Founder',
    'Marketing Leader',
    'Rev Ops Leader'
  ];

  // Comprehensive job titles list (500+ titles for B2B prospecting)
  const jobTitles = [
    'A/B Testing Consultant',
    'AI Engineer',
    'AI Marketing Consultant',
    'API Design Influencer Brand Consultant',
    'API Marketing Manager',
    'Ability Point Design Influencer Brand Consultant',
    'Acceptance Design Influencer Brand Consultant',
    'Access Point Design Influencer Brand Consultant',
    'Accessibility Design Influencer Brand Consultant',
    'Accomplishment Effort Point Design Influencer Brand Consultant',
    'Account Executive',
    'Account Manager',
    'Acquisition Marketing Manager',
    'Advertising Manager',
    'Analytics Manager',
    'Brand Manager',
    'Business Development Manager',
    'Campaign Manager',
    'Chief Executive Officer',
    'Chief Marketing Officer',
    'Chief Revenue Officer',
    'Chief Technology Officer',
    'Content Marketing Manager',
    'Customer Success Manager',
    'Data Analyst',
    'Data Scientist',
    'Digital Marketing Manager',
    'Director of Marketing',
    'Director of Sales',
    'Email Marketing Manager',
    'Engineering Manager',
    'Growth Marketing Manager',
    'Head of Marketing',
    'Head of Sales',
    'Inside Sales Representative',
    'Lead Generation Specialist',
    'Marketing Analyst',
    'Marketing Automation Manager',
    'Marketing Coordinator',
    'Marketing Director',
    'Marketing Manager',
    'Marketing Operations Manager',
    'Marketing Specialist',
    'Operations Manager',
    'Outbound Sales Representative',
    'Performance Marketing Manager',
    'Product Manager',
    'Product Marketing Manager',
    'Revenue Operations Manager',
    'Sales Development Representative',
    'Sales Director',
    'Sales Manager',
    'Sales Operations Manager',
    'SEO Manager',
    'SEO Specialist',
    'Social Media Manager',
    'Software Engineer',
    'Vice President of Marketing',
    'Vice President of Sales',
    'Web Developer'
    // Add more job titles as needed...
  ];

  /**
   * Handle job title selection
   */
  const handleJobTitleSelect = (jobTitle: string) => {
    setSelectedJobTitle(jobTitle);
    setSearchTerm(jobTitle);
    setShowDropdown(false);
    setFocusedIndex(-1);
  };

  /**
   * Filter job titles based on search term
   */
  const filteredJobTitles = jobTitles.filter(title =>
    title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Handle search input changes
   */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(true);
    setFocusedIndex(-1);
    
    // Auto-select if exact match
    const exactMatch = jobTitles.find(title => 
      title.toLowerCase() === value.toLowerCase()
    );
    if (exactMatch) {
      setSelectedJobTitle(exactMatch);
    }
  };

  /**
   * Handle keyboard navigation in dropdown
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredJobTitles.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredJobTitles.length) {
          handleJobTitleSelect(filteredJobTitles[focusedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setFocusedIndex(-1);
        break;
    }
  };

  /**
   * Generate newsletters for selected job title and target audience
   */
  const handleGenerateNewsletters = async () => {
    if (!selectedTargetAudience.trim() || !selectedJobTitle.trim()) return;

    setIsGenerating(true);

    // Create new newsletter row
    const newRow: NewsletterRow = {
      id: `newsletter-${Date.now()}`,
      targetAudience: selectedTargetAudience.trim(),
      jobTitle: selectedJobTitle.trim(),
      status: 'running',
      progress: '🔍 Analyzing Pythia MCP for email performance data...',
      newsletters: [],
      createdAt: new Date()
    };

    setNewsletterRows(prev => [newRow, ...prev]);

    let jobId: string | null = null;

    try {
      // Progress update helper
      const updateProgress = (step: string) => {
        setNewsletterRows(prev => prev.map(row => 
          row.id === newRow.id 
            ? { ...row, progress: step }
            : row
        ));
      };

      // Step 1: Start async job (NO TIMEOUT - returns immediately)
      updateProgress('🚀 Starting newsletter generation...');
      
      const response = await fetch(API_ENDPOINTS.emailNewsletter.generateAsync, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          targetAudience: selectedTargetAudience.trim(),
          jobTitle: selectedJobTitle.trim(),
          options: {
            count: 3,
            ctaPreference: ['Find Your Perfect Tree →', 'Browse Our Trees →', 'Schedule a Demo →']
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to start generation`);
      }

      const responseData = await response.json();
      jobId = responseData.jobId;
      console.log('📋 Newsletter generation job started:', jobId);

      if (!jobId) {
        throw new Error('No job ID returned from server');
      }

      // Store jobId for resume after refresh
      setRunningJobs(prev => {
        const newMap = new Map(prev);
        newMap.set(newRow.id, jobId!);
        return newMap;
      });
      activePollingRef.current.add(jobId);

      // Step 2: Poll for completion (same pattern as blog creator)
      let attempts = 0;
      let transientErrorStreak = 0;
      const maxTransientErrorStreak = 8;
      const maxAttempts = 900; // 15 minutes max (same as blog creator)
      let pollInterval = 2000; // 2 seconds
      
      while (attempts < maxAttempts) {
        // Exponential backoff for resource management (same as blog creator)
        const backoffDelay = Math.min(pollInterval * Math.pow(1.1, Math.floor(attempts / 10)), 5000);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        
        try {
          // Poll job status with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const statusResp = await fetch(API_ENDPOINTS.emailNewsletter.jobStatus(jobId), {
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          transientErrorStreak = 0; // reset on success
          
          if (!statusResp.ok) {
            throw new Error(`HTTP ${statusResp.status}`);
          }
          
          const statusData = await statusResp.json();
          const jobData = statusData.data;
          
          // Update progress from backend
          if (jobData.message) {
            updateProgress(jobData.message);
          }
          
          // Check if completed
          if (jobData.status === 'completed') {
            console.log('✅ Newsletter generation completed!');
            if (!jobData.result) {
              throw new Error('Job completed but no result available');
            }
            
            // Use the result
            const result = jobData.result;
            console.log('📧 Newsletter result:', result);

      // Parse CTAs from the first newsletter for default values
      let heroCTA = 'Find Your Perfect Tree →';
      let mainCTA = 'Browse Our Trees →';
      
      if (result.newsletters && result.newsletters.length > 0) {
        const firstNewsletter = result.newsletters[0];
        console.log(`🔍 Newsletter content to parse (first 1000 chars):`, firstNewsletter.substring(0, 1000));
        const lines = firstNewsletter.split('\n');
        console.log(`🔍 Total lines in newsletter:`, lines.length);
        console.log(`🔍 Last 10 lines:`, lines.slice(-10));
        
        // Extract Hero CTA with bulletproof approach
        // Why this matters: Checks both same-line (after colon) and next-line CTA formats
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim().toLowerCase();
          if (line.includes('hero cta button text')) {
            console.log(`🔍 Found Hero CTA label at line ${i}: "${lines[i]}"`);
            
            // Check if CTA is on the same line after colon
            const originalLine = lines[i].trim();
            const colonIndex = originalLine.toLowerCase().indexOf('text:');
            if (colonIndex !== -1) {
              const sameLineCTA = originalLine.substring(colonIndex + 5).trim();
              if (sameLineCTA && sameLineCTA.length > 3) {
                heroCTA = sameLineCTA;
                console.log(`✅ Found Hero CTA (same line after 'text:'): "${heroCTA}"`);
                break;
              }
            }
            // Also check for simple colon
            const simpleColonIndex = originalLine.indexOf(':');
            if (simpleColonIndex !== -1 && originalLine.length > simpleColonIndex + 1) {
              const sameLineCTA = originalLine.substring(simpleColonIndex + 1).trim();
              if (sameLineCTA && sameLineCTA.length > 3 && !sameLineCTA.toLowerCase().includes('button')) {
                heroCTA = sameLineCTA;
                console.log(`✅ Found Hero CTA (same line after ':'): "${heroCTA}"`);
                break;
              }
            }
            
            // Look for CTA in next 5 lines
            for (let j = i + 1; j < Math.min(i + 6, lines.length); j++) {
              const ctaLine = lines[j].trim();
              console.log(`🔍 Checking line ${j} for Hero CTA: "${ctaLine}"`);
              if (ctaLine &&
                  !ctaLine.toLowerCase().includes('main cta') &&
                  !ctaLine.toLowerCase().includes('subject') &&
                  !ctaLine.toLowerCase().includes('pricing') &&
                  !ctaLine.toLowerCase().includes('white main section') &&
                  ctaLine.length > 3) {
                heroCTA = ctaLine;
                console.log(`✅ Found Hero CTA (next line): "${heroCTA}"`);
                break;
              }
            }
            break;
          }
        }
        
        // Extract Main CTA with bulletproof approach
        // Why this matters: Checks both same-line (after colon) and next-line CTA formats
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim().toLowerCase();
          if (line.includes('main cta button text')) {
            console.log(`🔍 Found Main CTA label at line ${i}: "${lines[i]}"`);
            
            // Check if CTA is on the same line after colon
            const originalLine = lines[i].trim();
            const colonIndex = originalLine.toLowerCase().indexOf('text:');
            if (colonIndex !== -1) {
              const sameLineCTA = originalLine.substring(colonIndex + 5).trim();
              if (sameLineCTA && sameLineCTA.length > 3) {
                mainCTA = sameLineCTA;
                console.log(`✅ Found Main CTA (same line after 'text:'): "${mainCTA}"`);
                break;
              }
            }
            // Also check for simple colon
            const simpleColonIndex = originalLine.indexOf(':');
            if (simpleColonIndex !== -1 && originalLine.length > simpleColonIndex + 1) {
              const sameLineCTA = originalLine.substring(simpleColonIndex + 1).trim();
              if (sameLineCTA && sameLineCTA.length > 3 && !sameLineCTA.toLowerCase().includes('button')) {
                mainCTA = sameLineCTA;
                console.log(`✅ Found Main CTA (same line after ':'): "${mainCTA}"`);
                break;
              }
            }
            
            // Look for CTA in next 5 lines
            for (let j = i + 1; j < Math.min(i + 6, lines.length); j++) {
              const ctaLine = lines[j].trim();
              console.log(`🔍 Checking line ${j} for Main CTA: "${ctaLine}"`);
              if (ctaLine &&
                  !ctaLine.toLowerCase().includes('hero cta') &&
                  !ctaLine.toLowerCase().includes('subject') &&
                  !ctaLine.toLowerCase().includes('pricing') &&
                  !ctaLine.toLowerCase().includes('white main section') &&
                  ctaLine.length > 3) {
                mainCTA = ctaLine;
                console.log(`✅ Found Main CTA (next line): "${mainCTA}"`);
                break;
              }
            }
            break;
          }
        }
      }

      // Log final CTA results
      console.log(`🎯 FINAL CTA RESULTS:`);
      console.log(`   Hero CTA: "${heroCTA}" (${heroCTA === 'Find Your Perfect Tree →' ? 'FALLBACK' : 'EXTRACTED'})`);
      console.log(`   Main CTA: "${mainCTA}" (${mainCTA === 'Browse Our Trees →' ? 'FALLBACK' : 'EXTRACTED'})`);
      console.log(`   Target Audience: "${selectedTargetAudience.trim()}"`);
      console.log(`   Job Title: "${selectedJobTitle.trim()}"`);

            // Update row with results including extracted CTAs
            setNewsletterRows(prev => prev.map(row =>
              row.id === newRow.id
                ? {
                    ...row,
                    status: 'completed' as const,
                    progress: 'Email sequences generated successfully',
                    newsletters: result.newsletters || [],
                    heroCTA,
                    mainCTA,
                    mcpData: result.mcpData
                  }
                : row
            ));

            // Clear selection for next generation
            setSearchTerm('');
            setSelectedJobTitle('');
            setSelectedTargetAudience('');
            
            // Clean up running jobs
            setRunningJobs(prev => {
              const m = new Map(prev);
              m.delete(newRow.id);
              return m;
            });
            activePollingRef.current.delete(jobId);
            
            // Break out of polling loop
            break;
          } else if (jobData.status === 'error') {
            throw new Error(jobData.error || 'Newsletter generation failed');
          }
          
          // Continue polling
          attempts++;
          
        } catch (error) {
          // Handle transient errors (network issues, etc.)
          transientErrorStreak++;
          console.warn(`⚠️ Poll attempt ${attempts} failed (streak: ${transientErrorStreak}):`, error);
          
          if (transientErrorStreak >= maxTransientErrorStreak) {
            throw new Error(`Newsletter generation polling failed after ${maxTransientErrorStreak} consecutive errors`);
          }
          
          // Increment attempts and continue
          attempts++;
        }
      }
      
      // If we exit the loop without completing, it's a timeout
      if (attempts >= maxAttempts) {
        throw new Error('Newsletter generation timed out after 15 minutes');
      }

    } catch (error) {
      console.error('Newsletter generation error:', error);
      
      // Update row with error
      setNewsletterRows(prev => prev.map(row => 
        row.id === newRow.id 
          ? {
              ...row,
              status: 'error' as const,
              progress: '❌ Generation failed',
              error: error instanceof Error ? error.message : 'Unknown error occurred'
            }
          : row
      ));
      
      // Clean up running jobs
      const currentJobId = runningJobs.get(newRow.id) || jobId || null;
      setRunningJobs(prev => {
        const m = new Map(prev);
        m.delete(newRow.id);
        return m;
      });
      if (currentJobId) {
        activePollingRef.current.delete(currentJobId);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Toggle row selection
   */
  const toggleRowSelection = (rowId: string) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  /**
   * Select all rows
   */
  const selectAllRows = () => {
    const allRowIds = new Set(newsletterRows.map(row => row.id));
    setSelectedRows(allRowIds);
  };

  /**
   * Clear all selections
   */
  const clearSelection = () => {
    setSelectedRows(new Set());
  };

  /**
   * Show confirmation modal for bulk deletion
   * Why this matters: Provides safety check before deleting selected newsletters
   */
  const showBulkDeleteConfirmation = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  /**
   * Delete selected rows after confirmation
   * Why this matters: Performs actual bulk deletion after user confirms
   */
  const confirmBulkDelete = () => {
    setNewsletterRows(prev => 
      prev.filter(row => !selectedRows.has(row.id))
    );
    setSelectedRows(new Set());
    setShowBulkDeleteModal(false);
  };

  /**
   * Cancel bulk deletion
   * Why this matters: Allows users to back out of the delete action
   */
  const cancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  /**
   * Open action modal for row
   */
  const openActionModal = (rowId: string) => {
    setActiveModalRowId(rowId);
    setIsActionModalOpen(true);
  };

  /**
   * Close action modal
   */
  const closeActionModal = () => {
    setIsActionModalOpen(false);
    setActiveModalRowId(null);
  };

  /**
   * Handle content update from modal
   */
  const handleContentUpdate = (rowId: string, updatedNewsletters: string[], heroCTA?: string, mainCTA?: string) => {
    setNewsletterRows(prev => prev.map(row => {
      if (row.id === rowId) {
        const updatedRow: NewsletterRow = {
          ...row,
          newsletters: updatedNewsletters
        };
        if (heroCTA !== undefined) updatedRow.heroCTA = heroCTA;
        if (mainCTA !== undefined) updatedRow.mainCTA = mainCTA;
        return updatedRow;
      }
      return row;
    }));
  };


  /**
   * Save newsletter rows to localStorage whenever they change
   * Also saves runningJobs so polling can resume after refresh
   */
  useEffect(() => {
    saveNewslettersToStorage(newsletterRows, runningJobs);
  }, [newsletterRows, runningJobs]);

  /**
   * resumeJobPolling
   * Why this matters: Resumes polling for a specific job after page reload,
   * using the same polling logic as handleGenerateNewsletters but without starting a new job.
   * This allows content generation to continue seamlessly after refresh.
   */
  const resumeJobPolling = async (jobId: string, rowId: string) => {
    const row = newsletterRows.find(r => r.id === rowId);
    if (!row) {
      console.warn(`⚠️ Row ${rowId} not found for job ${jobId}, removing from running jobs`);
      setRunningJobs(prev => {
        const newMap = new Map(prev);
        newMap.delete(rowId);
        return newMap;
      });
      return;
    }

    // Only resume if row is still in running state
    if (row.status !== 'running') {
      console.log(`📋 Row ${rowId} no longer running (${row.status}), removing job ${jobId}`);
      setRunningJobs(prev => {
        const newMap = new Map(prev);
        newMap.delete(rowId);
        return newMap;
      });
      return;
    }

    // Prevent duplicate polling
    if (activePollingRef.current.has(jobId)) {
      console.log(`⏭️ Job ${jobId} already being polled, skipping resume`);
      return;
    }

    console.log(`🔄 Resuming job ${jobId} for ${row.jobTitle}`);
    activePollingRef.current.add(jobId);

    try {
      // Progress update helper
      const updateProgress = (step: string) => {
        setNewsletterRows(prev => prev.map(r => 
          r.id === rowId 
            ? { ...r, progress: step }
            : r
        ));
      };

      // Poll for completion (same logic as handleGenerateNewsletters)
      let attempts = 0;
      let transientErrorStreak = 0;
      const maxTransientErrorStreak = 8;
      const maxAttempts = 900; // 15 minutes max
      let pollInterval = 2000; // 2 seconds
      
      while (attempts < maxAttempts) {
        // Exponential backoff for resource management
        const backoffDelay = Math.min(pollInterval * Math.pow(1.1, Math.floor(attempts / 10)), 5000);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        
        try {
          // Poll job status with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
          
          const statusResp = await fetch(API_ENDPOINTS.emailNewsletter.jobStatus(jobId), {
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          transientErrorStreak = 0; // reset on success
          
          if (!statusResp.ok) {
            throw new Error(`HTTP ${statusResp.status}`);
          }
          
          const statusData = await statusResp.json();
          const jobData = statusData.data;
          
          // Update progress from backend
          if (jobData.message) {
            updateProgress(jobData.message);
          }
          
          // Check if completed
          if (jobData.status === 'completed') {
            console.log('✅ Newsletter generation completed!');
            if (!jobData.result) {
              throw new Error('Job completed but no result available');
            }
            
            // Use the result
            const result = jobData.result;
            console.log('📧 Newsletter result:', result);

            // Parse CTAs from the first newsletter for default values
            let heroCTA = 'Find Your Perfect Tree →';
            let mainCTA = 'Browse Our Trees →';
            
            if (result.newsletters && result.newsletters.length > 0) {
              const firstNewsletter = result.newsletters[0];
              const lines = firstNewsletter.split('\n');
              
              // Extract Hero CTA
              for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim().toLowerCase();
                if (line.includes('hero cta button text')) {
                  const originalLine = lines[i].trim();
                  const colonIndex = originalLine.toLowerCase().indexOf('text:');
                  if (colonIndex !== -1) {
                    const sameLineCTA = originalLine.substring(colonIndex + 5).trim();
                    if (sameLineCTA && sameLineCTA.length > 3) {
                      heroCTA = sameLineCTA;
                      break;
                    }
                  }
                  const simpleColonIndex = originalLine.indexOf(':');
                  if (simpleColonIndex !== -1 && originalLine.length > simpleColonIndex + 1) {
                    const sameLineCTA = originalLine.substring(simpleColonIndex + 1).trim();
                    if (sameLineCTA && sameLineCTA.length > 3 && !sameLineCTA.toLowerCase().includes('button')) {
                      heroCTA = sameLineCTA;
                      break;
                    }
                  }
                  for (let j = i + 1; j < Math.min(i + 6, lines.length); j++) {
                    const ctaLine = lines[j].trim();
                    if (ctaLine &&
                        !ctaLine.toLowerCase().includes('main cta') &&
                        !ctaLine.toLowerCase().includes('subject') &&
                        !ctaLine.toLowerCase().includes('pricing') &&
                        !ctaLine.toLowerCase().includes('white main section') &&
                        ctaLine.length > 3) {
                      heroCTA = ctaLine;
                      break;
                    }
                  }
                  break;
                }
              }
              
              // Extract Main CTA
              for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim().toLowerCase();
                if (line.includes('main cta button text')) {
                  const originalLine = lines[i].trim();
                  const colonIndex = originalLine.toLowerCase().indexOf('text:');
                  if (colonIndex !== -1) {
                    const sameLineCTA = originalLine.substring(colonIndex + 5).trim();
                    if (sameLineCTA && sameLineCTA.length > 3) {
                      mainCTA = sameLineCTA;
                      break;
                    }
                  }
                  const simpleColonIndex = originalLine.indexOf(':');
                  if (simpleColonIndex !== -1 && originalLine.length > simpleColonIndex + 1) {
                    const sameLineCTA = originalLine.substring(simpleColonIndex + 1).trim();
                    if (sameLineCTA && sameLineCTA.length > 3 && !sameLineCTA.toLowerCase().includes('button')) {
                      mainCTA = sameLineCTA;
                      break;
                    }
                  }
                  for (let j = i + 1; j < Math.min(i + 6, lines.length); j++) {
                    const ctaLine = lines[j].trim();
                    if (ctaLine &&
                        !ctaLine.toLowerCase().includes('hero cta') &&
                        !ctaLine.toLowerCase().includes('subject') &&
                        !ctaLine.toLowerCase().includes('pricing') &&
                        !ctaLine.toLowerCase().includes('white main section') &&
                        ctaLine.length > 3) {
                      mainCTA = ctaLine;
                      break;
                    }
                  }
                  break;
                }
              }
            }

            // Update row with results including extracted CTAs
            setNewsletterRows(prev => prev.map(r =>
              r.id === rowId
                ? {
                    ...r,
                    status: 'completed' as const,
                    progress: 'Email sequences generated successfully',
                    newsletters: result.newsletters || [],
                    heroCTA,
                    mainCTA,
                    mcpData: result.mcpData
                  }
                : r
            ));

            // Clean up running jobs
            setRunningJobs(prev => {
              const m = new Map(prev);
              m.delete(rowId);
              return m;
            });
            activePollingRef.current.delete(jobId);
            return;
          } else if (jobData.status === 'error') {
            throw new Error(jobData.error || 'Newsletter generation failed');
          }
          
          // Continue polling
          attempts++;
          
        } catch (error) {
          // Handle transient errors (network issues, etc.)
          transientErrorStreak++;
          console.warn(`⚠️ Poll attempt ${attempts} failed (streak: ${transientErrorStreak}):`, error);
          
          if (transientErrorStreak >= maxTransientErrorStreak) {
            throw new Error(`Newsletter generation polling failed after ${maxTransientErrorStreak} consecutive errors`);
          }
          
          // Increment attempts and continue
          attempts++;
        }
      }
      
      // If we exit the loop without completing, it's a timeout
      if (attempts >= maxAttempts) {
        throw new Error('Newsletter generation timed out after 15 minutes');
      }
    } catch (err: any) {
      console.error(`❌ Error resuming job ${jobId} for ${row.jobTitle}:`, err?.message || err);

      setNewsletterRows(prev => prev.map(r =>
        r.id === rowId
          ? {
              ...r,
              status: 'error' as const,
              progress: '❌ Generation failed',
              error: err?.message || 'Unknown error occurred'
            }
          : r
      ));

      setRunningJobs(prev => {
        const m = new Map(prev);
        m.delete(rowId);
        return m;
      });
      activePollingRef.current.delete(jobId);
    }
  };

  /**
   * resumeRunningJobs
   * Why this matters: After page reload, resume polling for any jobs that were running
   * when the user left the page, ensuring content generation continues seamlessly.
   * Staggers resume calls to prevent browser resource exhaustion.
   */
  useEffect(() => {
    if (runningJobs.size === 0 || newsletterRows.length === 0) return;

    console.log(`🔄 [EmailNewsletter] Resuming polling for ${runningJobs.size} running jobs`);
    
    // Stagger resume calls by 500ms each to prevent ERR_INSUFFICIENT_RESOURCES
    let delayMs = 0;
    runningJobs.forEach((jobId, rowId) => {
      // Check if this job is already being polled
      if (activePollingRef.current.has(jobId)) {
        console.log(`⏭️ Job ${jobId} already being polled, skipping resume`);
        return;
      }

      const row = newsletterRows.find(r => r.id === rowId);
      if (!row || row.status !== 'running') {
        console.log(`📋 Row ${rowId} not found or not running, removing job ${jobId}`);
        setRunningJobs(prev => {
          const newMap = new Map(prev);
          newMap.delete(rowId);
          return newMap;
        });
        return;
      }

      setTimeout(() => {
        resumeJobPolling(jobId, rowId);
      }, delayMs);
      
      delayMs += 500; // Stagger by 500ms
    });
  }, [runningJobs, newsletterRows]);


  /**
   * Handle click outside dropdown to close
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setFocusedIndex(-1);
      }
      if (targetDropdownRef.current && !targetDropdownRef.current.contains(event.target as Node)) {
        setShowTargetDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Style object for clipped content (consistent with CompetitorConquestingPage)
  const clippedStyle = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    display: 'block',
    width: '100%'
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', padding: '2rem' }}>
        {/* Header Section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '0.5rem'
          }}>
            Lifecycle Agents
          </h1>
          <p style={{
            fontSize: '0.875rem',
            color: '#6b7280'
          }}>
            Create email sequences that improve engagement rates using US Citrus's engagement data and AI personalization.
          </p>
        </div>


        {/* Controls Section */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1.5rem',
          background: '#f9fafb',
          borderRadius: '0.75rem',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            {/* Target Audience Dropdown */}
            <div style={{ position: 'relative' }} ref={targetDropdownRef}>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem', fontWeight: '500' }}>
                Prospect Type
              </label>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                Which type of US Citrus customer are you targeting? (e.g., Home gardeners, Commercial growers, Citrus enthusiasts etc.)
              </p>
              <button
                onClick={() => setShowTargetDropdown(!showTargetDropdown)}
                style={{
                  width: '80%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  background: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: selectedTargetAudience ? '#111827' : '#6b7280',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                  boxShadow: showTargetDropdown ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!showTargetDropdown) {
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showTargetDropdown) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }
                }}
              >
                <span>{selectedTargetAudience || 'Select prospect type...'}</span>
                <span style={{ transform: showTargetDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>▼</span>
              </button>

              {showTargetDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  right: '0',
                  marginTop: '-3rem',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  zIndex: 50,
                  overflow: 'hidden'
                }}>
                  {targetAudiences.map((audience, index) => (
                    <button
                      key={audience}
                      onClick={() => {
                        setSelectedTargetAudience(audience);
                        setShowTargetDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '0.75rem',
                        border: 'none',
                        background: selectedTargetAudience === audience ? '#f3f4f6' : 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: '#374151',
                        borderBottom: index < targetAudiences.length - 1 ? '1px solid #f3f4f6' : 'none',
                        transition: 'background-color 0.15s ease-in-out'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedTargetAudience !== audience) {
                          e.currentTarget.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = selectedTargetAudience === audience ? '#f3f4f6' : 'white';
                      }}
                    >
                      {audience}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Job Title Dropdown with Generate Button */}
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <label style={{ display: 'block', fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem', fontWeight: '500' }}>
                Job Title They Want to Reach
              </label>
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                Which customer segment are you targeting? (e.g., Home gardeners, Commercial growers)
              </p>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleKeyDown}
                placeholder="Search for prospect job title (e.g., Chief Executive Officer, VP Sales)"
                style={{
                  width: '80%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  background: 'white',
                  fontSize: '0.875rem',
                  marginBottom: '1rem'
                }}
              />

              {showDropdown && filteredJobTitles.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% - 1rem)',
                  left: '0',
                  right: '0',
                  marginTop: '-2rem',
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 50,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {filteredJobTitles.slice(0, 10).map((title, index) => (
                    <button
                      key={title}
                      onClick={() => handleJobTitleSelect(title)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '0.5rem 0.75rem',
                        border: 'none',
                        background: index === focusedIndex ? '#f3f4f6' : 'transparent',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                      onMouseEnter={() => setFocusedIndex(index)}
                    >
                      {title}
                    </button>
                  ))}
                  {filteredJobTitles.length > 10 && (
                    <div style={{
                      padding: '0.5rem 0.75rem',
                      fontSize: '0.75rem',
                      color: '#6b7280',
                      borderTop: '1px solid #f3f4f6'
                    }}>
                      {filteredJobTitles.length - 10} more results...
                    </div>
                  )}
                </div>
              )}

              {/* Generate Button and Status - positioned below Job Title input */}
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={handleGenerateNewsletters}
                  disabled={!selectedTargetAudience.trim() || !selectedJobTitle.trim() || isGenerating}
                  style={{
                    padding: '0.625rem 1.25rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    background: (!selectedTargetAudience.trim() || !selectedJobTitle.trim() || isGenerating) ? '#e5e7eb' : '#3AB981',
                    color: (!selectedTargetAudience.trim() || !selectedJobTitle.trim() || isGenerating) ? '#6b7280' : 'white',
                    cursor: (!selectedTargetAudience.trim() || !selectedJobTitle.trim() || isGenerating) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  {isGenerating ? <RefreshCw style={{ width: '1rem', height: '1rem', animation: 'pulse 1s infinite' }} /> : <FileText style={{ width: '1rem', height: '1rem' }} />}
                  {isGenerating ? 'Generating...' : `Generate Prospecting Emails to ${selectedTargetAudience || 'Prospects'}`}
                </button>

                {isGenerating && (
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Creating personalized email sequence...
                  </span>
                )}
                {!isGenerating && newsletterRows.length > 0 && (
                  <span style={{ fontSize: '0.875rem', color: '#3AB981' }}>
                    {newsletterRows.length.toLocaleString()} sequence{newsletterRows.length !== 1 ? 's' : ''} generated
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bulk controls */}
        {newsletterRows.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
            <button
              onClick={selectedRows.size > 0 ? clearSelection : selectAllRows}
              style={{
                padding: '0.4rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              {selectedRows.size > 0 ? `Unselect (${selectedRows.size})` : 'Select All'}
            </button>
            {selectedRows.size > 0 && (
              <button
                onClick={showBulkDeleteConfirmation}
                style={{
                  padding: '0.4rem 0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Delete Selected ({selectedRows.size})
              </button>
            )}
          </div>
        )}

        {/* Table Container */}
        {newsletterRows.length > 0 ? (
          <div style={{ backgroundColor: '#ffffff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
            <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
                <colgroup>
                  <col style={{ width: '32px' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '16%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>
                      <input
                        type="checkbox"
                        checked={newsletterRows.length > 0 && selectedRows.size === newsletterRows.length}
                        onChange={selectedRows.size > 0 ? clearSelection : selectAllRows}
                        style={{ cursor: 'pointer', marginTop: '4px' }}
                      />
                    </th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>PROSPECT</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>THEIR JOB TITLE OF INTEREST</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>STATUS</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>PROGRESS</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>CREATED</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.875rem' }}>
                  {newsletterRows.map((row) => (
                    <tr key={row.id}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(row.id)}
                          onChange={() => toggleRowSelection(row.id)}
                          style={{ marginTop: '8px' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={clippedStyle} title={row.targetAudience}>
                          {row.targetAudience}
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={clippedStyle} title={row.jobTitle}>{row.jobTitle}</div>
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        {row.status === 'running' ? (
                          <button
                            disabled
                            style={{ 
                              padding: '0.3125rem 0.625rem',
                              borderRadius: '0.5rem',
                              border: '1px solid #e5e7eb',
                              background: '#e5e7eb',
                              color: '#111827',
                              cursor: 'not-allowed',
                              fontSize: '0.75rem'
                            }}
                            title={row.progress || 'Email generation in progress...'}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#10b981',
                                animation: 'pulse 1.5s ease-in-out infinite'
                              }} />
                              Running…
                            </span>
                          </button>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            ...(row.status === 'completed' ? { background: '#d1fae5', color: '#065f46' } :
                               row.status === 'error' ? { background: '#fee2e2', color: '#dc2626' } :
                               { background: '#f3f4f6', color: '#374151' })
                          }}>
                            {row.status === 'error' && <AlertTriangle style={{ width: '0.75rem', height: '0.75rem' }} />}
                            {row.status === 'completed' && <Check style={{ width: '0.75rem', height: '0.75rem' }} />}
                            {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', color: '#374151' }}>
                        {row.error ? (
                          <span style={{ color: '#dc2626' }} title={row.error}>
                            {row.error.length > 50 ? `${row.error.substring(0, 50)}...` : row.error}
                          </span>
                        ) : row.status === 'completed' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} title={row.progress}>
                            <CheckCircle style={{ width: '1rem', height: '1rem', color: '#10b981', flexShrink: 0 }} />
                            <span style={clippedStyle}>{row.progress}</span>
                          </div>
                        ) : (
                          <div style={clippedStyle} title={row.progress}>{row.progress}</div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6', color: '#374151' }}>
                        {row.createdAt.toLocaleDateString()}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        {row.status === 'completed' && row.newsletters.length > 0 ? (
                          <button
                            onClick={() => openActionModal(row.id)}
                            style={{
                              padding: '0.3125rem 0.625rem',
                              borderRadius: '0.5rem',
                              border: '1px solid #e5e7eb',
                              background: '#EBF212',
                              color: '#000000',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                          >
                            <Users style={{ width: '0.75rem', height: '0.75rem' }} />
                            Manage
                          </button>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#6b7280'
          }}>
            <FileText style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
            <h3 style={{ marginBottom: '0.5rem', color: '#374151' }}>No email sequences generated yet</h3>
            <p style={{ marginBottom: '0.5rem' }}>Select a Prospect Type and the Job Title they want to reach.</p>
            <p style={{ fontSize: '0.875rem' }}>Generate personalized email sequences powered by US Citrus's engagement data.</p>
          </div>
        )}

        {/* Action Modal */}
        {isActionModalOpen && activeModalRowId && (
          <EmailNewsletterActionModal
            isOpen={isActionModalOpen}
            onClose={closeActionModal}
            newsletterRow={newsletterRows.find(row => row.id === activeModalRowId)!}
            onContentUpdate={handleContentUpdate}
          />
        )}

        {/* Bulk Delete Confirmation Modal */}
        {showBulkDeleteModal && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={cancelBulkDelete}
          >
            <div 
              style={{ 
                maxWidth: '500px', 
                width: '90%',
                position: 'relative',
                backgroundColor: '#ffffff',
                borderRadius: '0.75rem',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                padding: '1.5rem'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ 
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '50%',
                    backgroundColor: '#fef2f2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}>
                    <AlertTriangle style={{ width: '1.5rem', height: '1.5rem', color: '#dc2626' }} />
                  </div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: '0 0 0.5rem', color: '#1f2937' }}>
                    Delete Selected Email Sequences?
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                    This action will permanently delete {selectedRows.size} selected email sequence{selectedRows.size > 1 ? 's' : ''}. This cannot be undone.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={cancelBulkDelete}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      background: '#ffffff',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmBulkDelete}
                    style={{ 
                      padding: '0.5rem 1rem',
                      border: 'none',
                      borderRadius: '0.5rem',
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    Delete Sequences
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EmailNewsletterPage;